import { HttpErrorResponse } from '@angular/common/http';

import { shouldSkipAuthRouteSessionProbe } from '../supertokens-config';

import { BROWSER_SESSION_ID_HEADER, getBrowserSessionId } from './browser-session-id';
import { FrontendErrorStore } from './frontend-error.store';

import type { FrontendErrorContext, FrontendErrorRecord } from './frontend-error.model';
import type { FrontendErrorTransport } from './frontend-error.transport';

type BrowserLocation = {
  href?: string;
  pathname?: string;
};

type BrowserWindow = {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
};

type ReporterOptions = {
  store: FrontendErrorStore;
  transport: FrontendErrorTransport;
  getLocation?: () => BrowserLocation;
  getSessionStorage?: () => Storage | null;
  getUserAgent?: () => string | undefined;
  getAppContext?: () => Partial<FrontendErrorRecord>;
  onEntryCaptured?: (entry: FrontendErrorRecord) => void;
  now?: () => string;
  createId?: () => string;
  dedupeWindowMs?: number;
  reloadPage?: () => void;
  onRecoveryRequired?: (reason: 'chunk-load' | 'i18n-bootstrap-failure') => void;
};

const DEFAULT_DEDUPE_WINDOW_MS = 1000;
const CHUNK_RELOAD_STORAGE_KEY_PREFIX = 'nt25.frontend.chunkReloadAttempted';
const I18N_RECOVERY_DIALOG_STORAGE_KEY_PREFIX = 'nt25.frontend.i18nRecoveryDialogShown';
const I18N_FAILURE_WINDOW_MS = 10_000;
const I18N_FAILURES_FOR_RECOVERY = 2;

export class FrontendErrorReporter {
  private readonly getLocation: () => BrowserLocation;
  private readonly getSessionStorage: () => Storage | null;
  private readonly getUserAgent: () => string | undefined;
  private readonly getAppContext: () => Partial<FrontendErrorRecord>;
  private readonly now: () => string;
  private readonly createId: () => string;
  private readonly reloadPage: () => void;
  private readonly recentFingerprints = new Map<string, number>();
  private readonly recentI18nFailures = new Map<string, number>();
  private originalConsoleError?: (...args: unknown[]) => void;

  constructor(private readonly options: ReporterOptions) {
    this.getLocation = options.getLocation ?? (() => ({ href: undefined, pathname: undefined }));
    this.getSessionStorage = options.getSessionStorage ?? getSessionStorageSafely;
    this.getUserAgent = options.getUserAgent ?? (() => undefined);
    this.getAppContext = options.getAppContext ?? (() => ({}));
    this.now = options.now ?? (() => new globalThis.Date().toISOString());
    this.createId =
      options.createId ??
      (() => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          return crypto.randomUUID();
        }

        return `frontend-error-${globalThis.Date.now()}`;
      });
    this.reloadPage = options.reloadPage ?? (() => globalThis.location?.reload());
  }

  getEntries(): FrontendErrorRecord[] {
    return this.options.store.getEntries();
  }

  clear(): void {
    this.options.store.clear();
  }

  writeToConsole(...args: unknown[]): void {
    if (this.originalConsoleError) {
      this.originalConsoleError(...args);
      return;
    }

    console.error(...args);
  }

  async capture(error: unknown, context: FrontendErrorContext): Promise<FrontendErrorRecord | null> {
    const entry = this.createRecord(error, context);
    this.applyChunkLoadRecoveryMetadata(entry);
    const fingerprint = this.createFingerprint(entry);

    if (this.isDuplicate(fingerprint, entry.timestamp)) {
      return null;
    }

    this.options.store.add(entry);
    this.options.onEntryCaptured?.(entry);
    await this.options.transport.send(entry);
    if (entry.chunkReloadAttempted) {
      this.reloadPage();
    } else {
      this.requestRecoveryIfNeeded(entry);
    }
    return entry;
  }

  installConsoleErrorCapture(consoleRef: Pick<Console, 'error'> = console): () => void {
    const original = consoleRef.error.bind(consoleRef);
    this.originalConsoleError = original;

    (consoleRef as { error: (...args: unknown[]) => void }).error = (...args: unknown[]) => {
      const primaryError = args.find((candidate) => candidate instanceof Error) ?? args[0] ?? 'console.error';
      void this.capture(primaryError, {
        source: 'console-error',
        consoleArgs: args
      });
      original(...args);
    };

    return () => {
      (consoleRef as { error: (...args: unknown[]) => void }).error = original;
      this.originalConsoleError = undefined;
    };
  }

  installGlobalListeners(windowRef: BrowserWindow): () => void {
    const onError = ((event: ErrorEvent) => {
      void this.capture(event.error ?? event.message, {
        source: 'window-error',
        filename: event.filename,
        lineNumber: event.lineno,
        columnNumber: event.colno
      });
    }) as EventListener;

    const onUnhandledRejection = ((event: PromiseRejectionEvent) => {
      void this.capture(event.reason, {
        source: 'unhandled-promise-rejection'
      });
    }) as EventListener;

    windowRef.addEventListener('error', onError);
    windowRef.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      windowRef.removeEventListener('error', onError);
      windowRef.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }

  installFetchCapture(fetchRef: typeof fetch = fetch, assignPatchedFetch?: (patchedFetch: typeof fetch) => void): () => void {
    if (typeof fetchRef !== 'function' || !assignPatchedFetch) {
      return () => undefined;
    }

    const originalFetch = fetchRef;
    const patchedFetch: typeof fetch = async (input, init) => {
      const requestUrl = readFetchRequestUrl(input);
      const pathname = this.getLocation().pathname ?? '';

      if (shouldShortCircuitSessionRefresh(requestUrl, pathname)) {
        return createUnauthorizedJsonResponse();
      }

      try {
        const response = await originalFetch(input, withBrowserSessionHeader(input, init));

        if (!response.ok && !shouldSkipFetchCapture(requestUrl, response.status)) {
          const detail = await readFetchErrorDetail(response.clone());
          await this.capture(detail.error ?? new Error(detail.message), {
            source: 'http',
            requestUrl,
            statusCode: response.status,
            statusText: response.statusText,
            extra: detail.payload
          });
        }

        return response;
      } catch (error) {
        if (!shouldSkipFetchCapture(requestUrl)) {
          await this.capture(error, {
            source: 'http',
            requestUrl
          });
        }

        throw error;
      }
    };

    assignPatchedFetch(patchedFetch);

    return () => {
      assignPatchedFetch(originalFetch);
    };
  }

  private createRecord(error: unknown, context: FrontendErrorContext): FrontendErrorRecord {
    const location = this.getLocation();
    const baseRecord: FrontendErrorRecord = {
      id: this.createId(),
      source: context.source,
      message: 'Unknown frontend error',
      logLevel: context.logLevel ?? 'error',
      timestamp: this.now(),
      href: location.href,
      pathname: location.pathname,
      userAgent: this.getUserAgent(),
      recoveryReason: context.recoveryReason,
      recoveryAction: context.recoveryAction,
      ...this.getAppContext()
    };

    if (error instanceof HttpErrorResponse) {
      const backendMessage = readMessage(error.error);

      return {
        ...baseRecord,
        name: error.name,
        message: backendMessage ?? error.message ?? `HTTP ${error.status}`,
        details: safeStringify(error.error),
        stack: error.error instanceof Error ? error.error.stack : undefined,
        requestUrl: error.url ?? context.requestUrl,
        statusCode: error.status || context.statusCode,
        statusText: error.statusText || context.statusText
      };
    }

    if (error instanceof Error) {
      return {
        ...baseRecord,
        name: error.name,
        message: error.message || baseRecord.message,
        stack: error.stack,
        details: safeStringify(context.extra),
        requestUrl: context.requestUrl,
        statusCode: context.statusCode,
        statusText: context.statusText,
        filename: context.filename,
        lineNumber: context.lineNumber,
        columnNumber: context.columnNumber,
        consoleArgs: normalizeConsoleArgs(context.consoleArgs)
      };
    }

    const fallbackMessage = readMessage(error);

    return {
      ...baseRecord,
      message: fallbackMessage ?? baseRecord.message,
      details: safeStringify(context.extra ?? error),
      requestUrl: context.requestUrl,
      statusCode: context.statusCode,
      statusText: context.statusText,
      filename: context.filename,
      lineNumber: context.lineNumber,
      columnNumber: context.columnNumber,
      consoleArgs: normalizeConsoleArgs(context.consoleArgs)
    };
  }

  private createFingerprint(entry: FrontendErrorRecord): string {
    return [entry.source, entry.message, entry.statusCode, entry.requestUrl, entry.pathname].map((part) => String(part ?? '')).join('|');
  }

  private isDuplicate(fingerprint: string, timestamp: string): boolean {
    const now = globalThis.Date.parse(timestamp);
    const lastSeen = this.recentFingerprints.get(fingerprint);

    this.recentFingerprints.set(fingerprint, now);

    if (lastSeen === undefined) {
      return false;
    }

    return now - lastSeen < (this.options.dedupeWindowMs ?? DEFAULT_DEDUPE_WINDOW_MS);
  }

  private applyChunkLoadRecoveryMetadata(entry: FrontendErrorRecord): void {
    if (!isFrontendChunkLoadError(entry)) {
      return;
    }

    const text = getFrontendErrorSearchText(entry);
    entry.chunkUrl = extractChunkUrl(text);
    entry.chunkReloadAttempted = this.reserveChunkReloadAttempt(entry);
    entry.logLevel = entry.chunkReloadAttempted ? 'warn' : 'error';
  }

  private reserveChunkReloadAttempt(entry: FrontendErrorRecord): boolean {
    const storage = this.getSessionStorage();
    if (!storage) {
      return false;
    }

    const storageKey = getChunkReloadStorageKey(entry);
    try {
      if (storage.getItem(storageKey) === 'true') {
        return false;
      }

      storage.setItem(storageKey, 'true');
      return true;
    } catch {
      return false;
    }
  }

  private requestRecoveryIfNeeded(entry: FrontendErrorRecord): void {
    if (isFrontendChunkLoadError(entry)) {
      this.options.onRecoveryRequired?.('chunk-load');
      return;
    }

    if (!isI18nAssetFailure(entry)) {
      return;
    }

    const now = globalThis.Date.parse(entry.timestamp);
    this.recentI18nFailures.set(entry.requestUrl!, now);
    for (const [url, timestamp] of this.recentI18nFailures) {
      if (now - timestamp > I18N_FAILURE_WINDOW_MS) {
        this.recentI18nFailures.delete(url);
      }
    }

    if (this.recentI18nFailures.size >= I18N_FAILURES_FOR_RECOVERY && this.reserveI18nRecoveryDialog(entry)) {
      this.options.onRecoveryRequired?.('i18n-bootstrap-failure');
    }
  }

  private reserveI18nRecoveryDialog(entry: FrontendErrorRecord): boolean {
    const storage = this.getSessionStorage();
    if (!storage) {
      return false;
    }

    const storageKey = [I18N_RECOVERY_DIALOG_STORAGE_KEY_PREFIX, entry.environment ?? 'unknown', entry.appVersion ?? 'unknown'].join(':');
    try {
      if (storage.getItem(storageKey) === 'true') {
        return false;
      }

      storage.setItem(storageKey, 'true');
      return true;
    } catch {
      return false;
    }
  }
}

function getSessionStorageSafely(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function getChunkReloadStorageKey(entry: FrontendErrorRecord): string {
  return [CHUNK_RELOAD_STORAGE_KEY_PREFIX, entry.environment ?? 'unknown', entry.appVersion ?? 'unknown'].join(':');
}

function isFrontendChunkLoadError(entry: FrontendErrorRecord): boolean {
  const text = getFrontendErrorSearchText(entry).toLowerCase();
  return (
    text.includes('failed to fetch dynamically imported module') ||
    text.includes('error loading dynamically imported module') ||
    text.includes('importing a module script failed') ||
    text.includes('chunkloaderror') ||
    /loading chunk [\w-]+ failed/.test(text)
  );
}

function isI18nAssetFailure(entry: FrontendErrorRecord): boolean {
  return entry.source === 'http' && entry.statusCode === undefined && !!entry.requestUrl?.includes('/assets/i18n/');
}

function getFrontendErrorSearchText(entry: FrontendErrorRecord): string {
  return [entry.name, entry.message, entry.stack, entry.details, ...(entry.consoleArgs ?? [])].filter(Boolean).join(' ');
}

function extractChunkUrl(text: string): string | undefined {
  const urls = text.match(/https?:\/\/[^\s"'<>`]+/g);
  return urls?.find((url) => url.includes('.js'));
}

function normalizeConsoleArgs(args: unknown[] | undefined): string[] | undefined {
  if (!args?.length) {
    return undefined;
  }

  return args.map((value) => {
    if (value instanceof Error) {
      return `${value.name}: ${value.message}`;
    }

    if (typeof value === 'string') {
      return value;
    }

    return safeStringify(value);
  });
}

function readFetchRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input.url;
}

function withBrowserSessionHeader(input: RequestInfo | URL, init: RequestInit | undefined): RequestInit | undefined {
  const browserSessionId = getBrowserSessionId();
  if (!browserSessionId) {
    return init;
  }

  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  if (!headers.has(BROWSER_SESSION_ID_HEADER)) {
    headers.set(BROWSER_SESSION_ID_HEADER, browserSessionId);
  }

  return { ...init, headers };
}

function shouldSkipFetchCapture(requestUrl: string, statusCode?: number): boolean {
  return (
    requestUrl.includes('/api/public/frontend-error') ||
    (requestUrl.includes('/api/auth/session/refresh') && statusCode === 401)
  );
}

function shouldShortCircuitSessionRefresh(requestUrl: string, pathname: string): boolean {
  if (!requestUrl.includes('/api/auth/session/refresh')) {
    return false;
  }

  const cookieHeader = typeof document === 'undefined' ? '' : (document.cookie ?? '');
  return shouldSkipAuthRouteSessionProbe(pathname, cookieHeader);
}

function createUnauthorizedJsonResponse(): Response {
  return new Response(JSON.stringify({ message: 'unauthorised' }), {
    status: 401,
    statusText: 'Unauthorized',
    headers: {
      'content-type': 'application/json'
    }
  });
}

async function readFetchErrorDetail(response: Response): Promise<{ message: string; error?: Error; payload?: unknown }> {
  const fallbackMessage = `HTTP ${response.status} ${response.statusText}`.trim();
  const contentType = response.headers.get('content-type') ?? '';

  try {
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { message?: string; details?: unknown };
      const details =
        typeof payload?.details === 'string'
          ? payload.details
          : payload?.details !== undefined
            ? safeStringify(payload.details)
            : undefined;
      const message = details || payload?.message || fallbackMessage;
      return {
        message,
        error: new Error(message),
        payload
      };
    }

    const text = await response.text();
    const message = text || fallbackMessage;

    return {
      message,
      error: new Error(message)
    };
  } catch {
    return {
      message: fallbackMessage,
      error: new Error(fallbackMessage)
    };
  }
}

function readMessage(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  if (value instanceof Error) {
    return value.message;
  }

  if (value && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
    return value.message;
  }

  return undefined;
}

function safeStringify(value: unknown): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  const seen = new WeakSet<object>();

  try {
    return JSON.stringify(value, (_key, currentValue: unknown) => {
      if (currentValue instanceof Error) {
        return {
          name: currentValue.name,
          message: currentValue.message,
          stack: currentValue.stack
        };
      }

      if (currentValue && typeof currentValue === 'object') {
        if (seen.has(currentValue)) {
          return '[Circular]';
        }
        seen.add(currentValue);
      }

      return currentValue;
    });
  } catch {
    return String(value);
  }
}
